import { Injectable, NgZone } from '@angular/core';
import { fromEvent, merge, Subscription, timer } from 'rxjs';
import {MatDialog} from "@angular/material/dialog";

@Injectable({
  providedIn: 'root'
})
export class AutoLogoutService {
  private readonly INACTIVITY_LIMIT = 30 * 60 * 1000; // 30 minutes in ms
  private readonly LAST_ACTIVITY_KEY = 'last-activity-time';
  private userActivitySubscription: Subscription | null = null;
  private otherTabActivitySubscription: Subscription | null = null;
  private inactivityTimerSubscription: Subscription | null = null;
  private otherTabActivityChannel: BroadcastChannel = new BroadcastChannel('reset-logout');
  constructor(private ngZone: NgZone, private dialog: MatDialog) {}

  startWatching(onTimeout: () => void) {
    this.stopWatching();

    // Run outside Angular to avoid triggering change detection on every event
    this.ngZone.runOutsideAngular(() => {
      if (this.getLastActivityTime() === null) {
        this.saveLastActivityTime(Date.now());
      }

      if (this.isSessionExpired()) {
        this.handleTimeout(onTimeout);
        return;
      }

      // Listen to user activity events
      const activityEvents$ = merge(
        fromEvent(window, 'mousemove'),
        fromEvent(window, 'mousedown'),
        fromEvent(window, 'click'),
        fromEvent(window, 'keydown'),
        fromEvent(window, 'scroll'),
        fromEvent(window, 'touchstart')
      );

      // Subscribe to user activity and reset timer
      this.userActivitySubscription = activityEvents$.subscribe(()=> {
        if (this.isSessionExpired()) {
          this.handleTimeout(onTimeout);
          return;
        }

        const activityTime = Date.now();
        this.saveLastActivityTime(activityTime);
        // Post message that this tab has been refreshed to other tabs.
        this.otherTabActivityChannel.postMessage(activityTime);
        this.resetTimer(onTimeout)
      });

      // If other tab has activity, refresh this tab.
      const broadcastEvents$ = fromEvent<MessageEvent<number>>(this.otherTabActivityChannel, 'message');
      this.otherTabActivitySubscription = broadcastEvents$.subscribe((event) => {
        const lastActivityTime = this.getLastActivityTime();
        if (typeof event.data === 'number' && (lastActivityTime === null || event.data > lastActivityTime)) {
          this.saveLastActivityTime(event.data);
        }
        this.resetTimer(onTimeout);
      })

      // Start the initial inactivity timer
      this.resetTimer(onTimeout);
    });
  }



  stopWatching() {
    this.userActivitySubscription?.unsubscribe();
    this.otherTabActivitySubscription?.unsubscribe();
    this.inactivityTimerSubscription?.unsubscribe();
    this.userActivitySubscription = null;
    this.otherTabActivitySubscription = null;
    this.inactivityTimerSubscription = null;
  }

  isSessionExpired(): boolean {
    const lastActivityTime = this.getLastActivityTime();
    return lastActivityTime !== null && Date.now() - lastActivityTime >= this.INACTIVITY_LIMIT;
  }

  clearLastActivity(): void {
    localStorage.removeItem(this.LAST_ACTIVITY_KEY);
  }

  private resetTimer(onTimeout: () => void) {
    this.inactivityTimerSubscription?.unsubscribe();
    const lastActivityTime = this.getLastActivityTime() ?? Date.now();
    const remainingTime = Math.min(
      this.INACTIVITY_LIMIT,
      Math.max(0, this.INACTIVITY_LIMIT - (Date.now() - lastActivityTime))
    );

    this.inactivityTimerSubscription = timer(remainingTime).subscribe(() => {
      // Another tab may have updated the shared activity time since this timer was scheduled.
      if (!this.isSessionExpired()) {
        this.resetTimer(onTimeout);
        return;
      }

      this.handleTimeout(onTimeout);
    });
  }

  private handleTimeout(onTimeout: () => void): void {
    this.ngZone.run(() => {
      onTimeout();
      this.dialog.closeAll();
    });
    this.stopWatching();
  }

  private getLastActivityTime(): number | null {
    const storedValue = localStorage.getItem(this.LAST_ACTIVITY_KEY);
    if (storedValue === null) {
      return null;
    }

    const lastActivityTime = Number(storedValue);
    return Number.isFinite(lastActivityTime) ? lastActivityTime : 0;
  }

  private saveLastActivityTime(activityTime: number): void {
    localStorage.setItem(this.LAST_ACTIVITY_KEY, activityTime.toString());
  }
}
